import { _ } from "../../../../core/polyglot/polyglot.js";
import {
	bimester,
	currentPeriodType,
	halfYear,
	month,
	quarter,
	year,
} from "../../../../models/period/PeriodType.js";
import AbstractFormulaTokenVisitor from "../../../../models/keyFigure/formulaToken/visitors/AbstractFormulaTokenVisitor.js";
import FormulaTokenLabelRenderer from "./FormulaTokenLabelRenderer.js";
import TextRepresentationDimensionSpecificationVisitor from "../../../../models/dimensionSpecification/visitors/TextRepresentationDimensionSpecificationVisitor.js";

const rollingLabels = {
	[currentPeriodType.getId()]: (rollingLength) =>
		_("As rolling {rollingLength} periods", {
			rollingLength,
		}),
	[month.getId()]: (rollingLength) =>
		_("As rolling {rollingLength} months", {
			rollingLength,
		}),
	[bimester.getId()]: (rollingLength) =>
		_("As rolling {rollingLength} bimesters", {
			rollingLength,
		}),
	[quarter.getId()]: (rollingLength) =>
		_("As rolling {rollingLength} quarters", {
			rollingLength,
		}),
	[halfYear.getId()]: (rollingLength) =>
		_("As rolling {rollingLength} half years", {
			rollingLength,
		}),
	[year.getId()]: (rollingLength) =>
		_("As rolling {rollingLength} years", {
			rollingLength,
		}),
};

const forwardShiftLabels = {
	[currentPeriodType.getId()]: (shift) =>
		_("One period forward", "{shift} periods forward", shift, {
			shift,
		}),
	[month.getId()]: (shift) =>
		_("One month forward", "{shift} months forward", shift, { shift }),
	[bimester.getId()]: (shift) =>
		_("One bimester forward", "{shift} bimesters forward", shift, {
			shift,
		}),
	[quarter.getId()]: (shift) =>
		_("One quarter forward", "{shift} quarters forward", shift, { shift }),
	[halfYear.getId()]: (shift) =>
		_("half a year forward", "{shift} half years forward", shift, {
			shift,
		}),
	[year.getId()]: (shift) =>
		_("One year forward", "{shift} years forward", shift, { shift }),
};

const backwardShiftLabels = {
	[currentPeriodType.getId()]: (shift) =>
		_("One period back", "{shift} periods back", shift, { shift }),
	[month.getId()]: (shift) =>
		_("One month back", "{shift} months back", shift, { shift }),
	[bimester.getId()]: (shift) =>
		_("One bimester back", "{shift} bimesters back", shift, { shift }),
	[quarter.getId()]: (shift) =>
		_("One quarter back", "{shift} quarters back", shift, { shift }),
	[halfYear.getId()]: (shift) =>
		_("half a year back", "{shift} half years back", shift, { shift }),
	[year.getId()]: (shift) =>
		_("One year back", "{shift} years back", shift, { shift }),
};

/**
 * Visitor that renders formula tokens in boxes for edition in a
 * `KeyFigureFormulaEditorWidget`.
 *
 * Below each token box, a list of properties can be displayed to show some
 * extra properties of a token, like it's dimension filtering or period
 * filtering.
 */
export default class FormulaTokenBoxRenderer extends AbstractFormulaTokenVisitor {
	constructor({ dimensionTree, onTokenSelected = () => {}, selectedToken }) {
		super(...arguments);
		this._dimensionTree = dimensionTree;
		this._selectedToken = selectedToken;
		this._onTokenSelected = onTokenSelected;
	}

	visitAbstractFormulaToken(token) {
		return this._tokenBoxRenderer({
			token,
			content: (html) => {
				html.div({ klass: "content" }, this._labelRenderer(token));
			},
		});
	}

	visitAbstractValueFormulaToken(token) {
		return this._tokenBoxRenderer({
			token,
			content: (html) => {
				html.div({ klass: "title" }, token.getLabel());
				html.div({ klass: "content" }, this._labelRenderer(token));
				html.render(this._propertiesRenderer(token));
			},
		});
	}

	_labelRenderer(token) {
		return new FormulaTokenLabelRenderer().visit(token);
	}

	_tokenBoxRenderer({ token, content }) {
		return (html) => {
			html.div(
				{
					klass: [
						"token",
						this._selectedCssClass(token),
						token.getType(),
					],
					id: token.getId(),
					click: () => this._onTokenSelected(token),
				},
				content,
			);
		};
	}

	_propertiesRenderer(token) {
		return (html) => {
			html.div(
				{ klass: "properties" },
				this._periodFilterPropertyRenderer(token),
				this._dimensionPropertyRenderer(token),
				this._periodTypePropertyRenderer(token),
				this._shiftPropertyRenderer(token),
				this._rollingPropertyRenderer(token),
			);
		};
	}

	_periodFilterPropertyRenderer(token) {
		return (html) => {
			if (!token.hasPeriodFilter()) {
				return;
			}
			html.div({ klass: "property" }, token.getPeriodFilter().getLabel());
		};
	}

	_dimensionPropertyRenderer(token) {
		return (html) => {
			if (!token.hasDimensionSpecification()) {
				return;
			}
			const div = html.div(
				{
					klass: "property",
					title: _("Dimension filter"),
					"data-content": this._dimensionText(
						token.getDimensionSpecification(),
					),
				},
				_("Filtered on dimensions"),
			);

			div.asJQuery().popover({
				trigger: "hover",
			});
		};
	}

	_periodTypePropertyRenderer(token) {
		return (html) => {
			const periodType = token.getPeriodType();
			if (!periodType || periodType.isCurrentPeriodType()) {
				return;
			}

			html.div(
				{ klass: "property" },
				_("Period type {periodType}", {
					periodType: periodType.getName(),
				}),
			);
		};
	}

	_shiftPropertyRenderer(token) {
		return (html) => {
			const shift = token.getShift();

			if (!shift) {
				return;
			}

			html.div(
				{ klass: "property" },
				this._shiftingModifierLabel({
					shift,
					periodType: token.getShiftPeriodType().getId(),
				}),
			);
		};
	}

	_rollingPropertyRenderer(token) {
		return (html) => {
			const isRolling = token.isRolling();
			const rollingLength = token.getRollingLength();
			const periodType = token.getRollingPeriodType();

			if (!isRolling || !rollingLength || !periodType) {
				return;
			}

			html.div(
				{ klass: "property" },
				this._rollingModifierLabel({
					shift: rollingLength,
					periodType: periodType.getId(),
				}),
			);
		};
	}

	_dimensionText(specification) {
		const visitor = new TextRepresentationDimensionSpecificationVisitor({
			dimensionTree: this._dimensionTree,
		});
		return visitor.visit(specification);
	}

	_selectedCssClass(token) {
		return this._isTokenSelected(token) ? "selected" : "";
	}

	_isTokenSelected(token) {
		return this._selectedToken === token;
	}

	_shiftingModifierLabel({ shift, periodType }) {
		const dictionary = shift > 0 ? forwardShiftLabels : backwardShiftLabels;
		return dictionary[periodType](Math.abs(shift));
	}

	_rollingModifierLabel({ shift, periodType }) {
		return rollingLabels[periodType](shift);
	}
}
